import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { PERKS_ADMIN_SLICE_NAME } from "./perksAdmin.constants"
import {
  createPerkAdmin,
  deletePerkAdmin,
  deletePerkComment,
  getPerksAdmin,
  getPerksAdminFilterOptions,
  getPerksManageOrder,
  reorderPerks,
  savePerkComment,
  togglePerkVisibility,
  updatePerkAdmin
} from "./perksAdmin.thunks"

/* ------------- Columns ------------- */

const columns = [
  { field_id: "is_visible", label: "Visibility", active: true },
  { field_id: "created", label: "Created", active: true },
  { field_id: "partner_name", label: "Partner name", active: true },
  { field_id: "partner_website", label: "Partner website", active: true },
  { field_id: "offer_details", label: "Offer details", active: true },
  { field_id: "description", label: "Description", active: true },
  { field_id: "url", label: "URL", active: true },
  { field_id: "promocode", label: "Promocode", active: true },
  { field_id: "comment", label: "Comments", active: true }
]

/* ------------- Initial State ------------- */

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumns: [...columns],
  columns: [...columns],

  filtersLastUpd: null,
  filters: {
    partner_name: "all",
    created: null
  },
  visibleFilters: { partner_name: true, created: true },

  saveFetching: false,
  saveResult: null,

  manageOrderFetching: false,
  manageOrderList: [],

  filterOptions: {
    partner_name: []
  }
}

/* ------------- Edits ------------- */

// Every edit shares one in-flight flag and differs only in the `saveResult` token the page reads
// to pick its notification and decide which modal to close, so the triplets are generated from
// that one mapping rather than written out seven times.
const saveThunks = [
  [createPerkAdmin, "perk_create"],
  [updatePerkAdmin, "perk_update"],
  [deletePerkAdmin, "perk_delete"],
  [togglePerkVisibility, "perk_visibility"],
  [reorderPerks, "perk_reorder"],
  [savePerkComment, "perk_comment"],
  [deletePerkComment, "perk_comment_delete"]
]

/* ------------- Slice ------------- */

const slice = createSlice({
  name: PERKS_ADMIN_SLICE_NAME,
  initialState,
  reducers: {
    // The legacy action took two arguments - `perksAdminSetProp(key, value)`. RTK actions carry a
    // single payload, so callers pass `perksAdminSetProp({ key, value })`; the container keeps the
    // two-argument call shape in its `mapDispatchToProps`.
    perksAdminSetProp(state, { payload: { key, value } }) {
      if (key === "filters") {
        state.filtersLastUpd = new Date().getTime()
        state.filters = { ...state.filters, ...value }
        return
      }

      if (key === "visibleFilters") {
        state.visibleFilters = { ...state.visibleFilters, ...value }
        return
      }

      state[key] = value
    },

    clearPerksAdminFilters(state) {
      state.filters = initialState.filters
    },

    resetPerksAdminSaveResult(state) {
      state.saveResult = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- table ---------------- */
      .addCase(getPerksAdmin.pending, (state) => {
        state.fetching = true
      })
      .addCase(getPerksAdmin.fulfilled, (state, { payload }) => {
        state.fetching = false
        state.result = payload
      })
      .addCase(getPerksAdmin.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- filter options ---------------- */
      .addCase(getPerksAdminFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions = {
          partner_name: Array.isArray(payload?.partner_name)
            ? payload.partner_name.map((p) => p?.label)
            : []
        }
      })
      // No toast on failure (the saga had `withoutErrorNotification`): the dropdown degrades to the
      // partner names of the rows already loaded instead.
      .addCase(getPerksAdminFilterOptions.rejected, (state) => {
        const names = (state.result?.results || []).map((r) => r.partner_name).filter(Boolean)

        state.filterOptions = { partner_name: Array.from(new Set(names)) }
      })

      /* ---------------- card order ---------------- */
      .addCase(getPerksManageOrder.pending, (state) => {
        state.manageOrderFetching = true
      })
      .addCase(getPerksManageOrder.fulfilled, (state, { payload }) => {
        state.manageOrderFetching = false
        state.manageOrderList = Array.isArray(payload) ? payload : payload?.results || []
      })
      .addCase(getPerksManageOrder.rejected, (state) => {
        state.manageOrderFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["perks-admin"]?.data
        if (!preference || preference.version !== PERKS_ADMIN_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columns || !preference.visibleFilters) return

        state.filters = preference.filters
        state.columns = preference.columns
        state.visibleFilters = preference.visibleFilters
      })

    /* ---------------- edits ---------------- */
    saveThunks.forEach(([thunk, saveResult]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saveFetching = true
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saveFetching = false
          state.saveResult = saveResult
        })
        .addCase(thunk.rejected, (state) => {
          state.saveFetching = false
        })
    })
  }
})

export const { perksAdminSetProp, clearPerksAdminFilters, resetPerksAdminSaveResult } =
  slice.actions

// Bump when the shape of the saved `{ version, filters, columns, visibleFilters }` payload changes.
export const PERKS_ADMIN_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state[PERKS_ADMIN_SLICE_NAME].filters, initialState.filters)

/* ------------- Persistence ------------- */

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: PERKS_ADMIN_SLICE_NAME,
    storage,
    // Only the user's own table setup survives a reload — the perks list, its filter options and
    // the manage-order list are all re-fetched, and `saveResult` is a one-shot notification token.
    whitelist: ["filters", "columns", "visibleFilters"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
